import classNames from "classnames";
import React, { ChangeEventHandler, FocusEventHandler } from "react";
import sanitizeHtml from "sanitize-html";
import checkmarkIcon from "../../assets/images/checkbox.svg";

export type ControlledCheckboxProps = {
  additionalClassName?: string;
  checked?: boolean;
  error?: boolean;
  id?: string;
  name?: string;
  label: string;
  readonly?: boolean;
  required?: boolean;
  value?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
};

const ControlledCheckbox = ({
  additionalClassName = "",
  checked,
  value,
  error,
  id,
  name,
  label,
  onBlur,
  onChange,
  readonly,
  required
}: ControlledCheckboxProps) => {
  let spanClassName: string;

  if (error) {
    spanClassName = "border-brand-tertiary-50 shadow-outline-error";

    if (checked) {
      spanClassName = classNames(spanClassName, "bg-brand-tertiary-50");
    } else {
      spanClassName = classNames(spanClassName, "bg-neutral-100");
    }
  } else {
    spanClassName = "focus:shadow-outline group-focus:shadow-outline";

    if (readonly) {
      spanClassName = classNames(spanClassName, "bg-input-bg-readonly");
    } else {
      spanClassName = classNames(spanClassName, "group-hover:border-brand-primary-80");

      if (checked) {
        spanClassName = classNames(
          spanClassName,
          "bg-brand-primary-70 group-hover:bg-brand-primary-80"
        );
      } else {
        spanClassName = classNames(
          spanClassName,
          "bg-neutral-100 border-brand-primary-dim-80"
        );
      }
    }
  }

  return (
    <label
      htmlFor={id}
      className={classNames(
        additionalClassName,
        "group flex items-center text-neutral-40 text-lg"
      )}
    >
      {readonly && (
        <input value={value} id={id} name={name} type="hidden" required={required} />
      )}

      <input
        checked={checked}
        className="opacity-0 w-0 h-0 absolute"
        disabled={readonly}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        required={required}
        type="checkbox"
        value={value}
      />

      {/* Don't put anything between the checkbox input and span, there's some css sibling styling going on here */}
      <span
        className={classNames(
          spanClassName,
          "checkbox inline-flex justify-center items-center flex-none w-8 h-8 mr-2 border rounded-md transition-all duration-100"
        )}
      >
        <svg
          width="20"
          height="20"
          className={classNames(
            value ? "text-neutral-100" : "text-transparent",
            "fill-current transition duration-100 pointer-events-none"
          )}
        >
          <use xlinkHref={checkmarkIcon.url} />
        </svg>
      </span>

      <span
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(label)
        }}
      />
    </label>
  );
};

export default ControlledCheckbox;
